/**
 * One message on screen (web-plan.md F1): the bubble, the service line, the
 * call card — the web twin of `components/message.rs`, wearing the same
 * classes because both clients wear one stylesheet (D57).
 */
import { For, type JSX, Show, createMemo } from "solid-js";

import type { ReactionGroup, Service } from "../gen/dto";
import { SERVICE_KINDS } from "../gen/dto";

import { type Block, type Run, blocksOf, hasUnknownEntities, runLink } from "../lib/entities";
import { Attachments } from "./attachments";
import type { Dict } from "../lib/i18n";
import { emojiCanonical } from "../lib/wasm";
import type { Bubble } from "../store/types";
import { PENDING_SEQ } from "../store/types";
import { Icon } from "./ds";

export interface BubbleActions {
  toggleReaction(seq: number, emoji: string): void;
  openMenu(seq: number, x: number, y: number): void;
}

export function BubbleView(props: {
  bubble: Bubble;
  t: Dict;
  actions: BubbleActions;
}): JSX.Element {
  const m = () => props.bubble.message;

  const hasVisualMedia = createMemo(
    () =>
      ((m().attachments?.length ?? 0) > 0 || props.bubble.localPreviews.length > 0) &&
      (m().attachments ?? []).every(
        (a) => a.kind === "photo" || a.kind === "video" || a.kind === "video_message",
      ),
  );
  // A caption keeps the text's own padding (bubble--text below); the media
  // above it instead gets the 1px frame `.message-bubble--captioned` gives it, so a
  // photo with words under it doesn't sit inset by the caption's padding too.
  const captioned = createMemo(() => m().text !== "" && hasVisualMedia());

  const cls = createMemo(() => {
    const parts = ["message-bubble"];
    if (props.bubble.isOwn) parts.push("message-bubble--own");
    const hasText = m().text !== "";
    const mediaOnly = !hasText && hasVisualMedia();
    if (mediaOnly) parts.push("message-bubble--media");
    if (captioned()) parts.push("message-bubble--captioned");
    if (hasText) parts.push("message-bubble--text");
    if (props.bubble.state === "failed") parts.push("message-bubble--failed");
    return parts.join(" ");
  });

  const unsupported = createMemo(
    () =>
      hasUnknownEntities(m().entities ?? []) ||
      (m().attachments ?? []).some((a) => !isKnownKind(a.kind)),
  );

  const blocks = createMemo<Block[]>(() => blocksOf(m().text, m().entities ?? []));

  return (
    <div
      class={cls()}
      id={m().seq === PENDING_SEQ ? undefined : `msg-${m().seq}`}
      onContextMenu={(e) => {
        if (m().seq === PENDING_SEQ) return;
        e.preventDefault();
        props.actions.openMenu(m().seq, e.clientX, e.clientY);
      }}
    >
      <Show when={props.bubble.replyPreview !== null}>
        <div class="reply-quote">
          <span class="reply-quote__author">{props.bubble.replyAuthor}</span>
          <span class="reply-quote__text">{props.bubble.replyPreview}</span>
        </div>
      </Show>

      <Show when={(m().attachments?.length ?? 0) > 0}>
        <Attachments channelId={m().channel_id} items={m().attachments ?? []} t={props.t} />
      </Show>
      <Show when={(m().attachments?.length ?? 0) === 0 && props.bubble.localPreviews.length > 0}>
        <div style="display:flex;gap:4px;flex-wrap:wrap">
          {props.bubble.localPreviews.map((url) => (
            <img
              src={url}
              alt=""
              style={`width:180px;max-width:100%;border-radius:12px;opacity:${props.bubble.state === "sent" ? 1 : 0.6}`}
            />
          ))}
        </div>
      </Show>
      <div class={captioned() ? "media-caption" : undefined}>
        <For each={blocks()}>{(block) => <BlockView block={block} />}</For>

        <Show when={unsupported()}>
          <div class="unsupported-media">
            <span class="unsupported-media__icon">
              <Icon name="help" size={15} />
            </span>
            <div>
              <span class="unsupported-media__text">{props.t.unsupportedFormatting()}</span>
            </div>
          </div>
        </Show>

        <Show
          when={props.bubble.reactions.length > 0}
          fallback={<Meta bubble={props.bubble} t={props.t} />}
        >
          <div class="message-bubble__footer">
            <div class="reaction-chips">
              <For each={props.bubble.reactions}>
                {(g) => (
                  <ReactionChip
                    group={g}
                    mine={props.bubble.myReaction === g.emoji}
                    onToggle={() => props.actions.toggleReaction(m().seq, g.emoji)}
                  />
                )}
              </For>
            </div>
            <Meta bubble={props.bubble} t={props.t} />
          </div>
        </Show>
      </div>
    </div>
  );
}

function ReactionChip(props: {
  group: ReactionGroup;
  mine: boolean;
  onToggle: () => void;
}): JSX.Element {
  // A value outside the vocabulary is shown (the count is still right) and
  // never clickable, never sent back (chat-api.md §7).
  const unknown = () => emojiCanonical(props.group.emoji) === null;
  return (
    <button
      type="button"
      class={`reaction-chip${props.mine ? " reaction-chip--mine" : ""}${unknown() ? " reaction-chip--unknown" : ""}`}
      onClick={() => {
        if (!unknown()) props.onToggle();
      }}
    >
      <span class="reaction-chip__emoji">{props.group.emoji}</span>
      <span class="reaction-chip__count">{props.group.count}</span>
    </button>
  );
}

function Meta(props: { bubble: Bubble; t: Dict }): JSX.Element {
  const time = () =>
    new Date(props.bubble.message.sent_at_ms).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  return (
    <span class="message-meta">
      <Show when={props.bubble.message.edited_at_ms != null}>
        <span class="message-meta__edited">{props.t.edited()}</span>
      </Show>
      <span class="message-meta__time">{time()}</span>
      <Show when={props.bubble.isOwn}>
        <Show
          when={props.bubble.state !== "failed"}
          fallback={<Icon name="error" size={13} class="echo-icon--failed" />}
        >
          <Icon
            name={props.bubble.state === "sent" ? "done" : "schedule"}
            size={13}
          />
        </Show>
      </Show>
    </span>
  );
}

function BlockView(props: { block: Block }): JSX.Element {
  const b = props.block;
  if (b.t === "pre") {
    return <div class="message-code-block selectable-text">{b.text}</div>;
  }
  if (b.t === "quote") {
    return (
      <div class="message-quote">
        <For each={b.runs}>{(run) => <RunView run={run} />}</For>
      </div>
    );
  }
  return <For each={b.runs}>{(run) => <RunView run={run} />}</For>;
}

function RunView(props: { run: Run }): JSX.Element {
  const link = () => runLink(props.run);
  const style = createMemo(() => {
    let s = "";
    for (const st of props.run.styles) {
      if (st.t === "bold") s += "font-weight:600;";
      else if (st.t === "italic") s += "font-style:italic;";
      else if (st.t === "underline") s += "text-decoration:underline;";
      else if (st.t === "strike") s += "text-decoration:line-through;";
    }
    return s === "" ? undefined : s;
  });
  const isCode = () => props.run.styles.some((s) => s.t === "code");
  const isSpoiler = () => props.run.styles.some((s) => s.t === "spoiler");
  const isMention = () => props.run.styles.some((s) => s.t === "mention");

  return (
    <Show
      when={link()}
      fallback={
        <Show
          when={isCode()}
          fallback={
            <Show
              when={isSpoiler()}
              fallback={
                <Show
                  when={isMention()}
                  fallback={
                    <span class="selectable-text" style={style()}>
                      {props.run.text}
                    </span>
                  }
                >
                  <span class="message-mention">{props.run.text}</span>
                </Show>
              }
            >
              <SpoilerRun text={props.run.text} />
            </Show>
          }
        >
          <span class="message-code">{props.run.text}</span>
        </Show>
      }
    >
      {(url) => (
        <a class="selectable-text" href={url()} target="_blank" rel="noreferrer noopener" style={style()}>
          {props.run.text}
        </a>
      )}
    </Show>
  );
}

function SpoilerRun(props: { text: string }): JSX.Element {
  let el!: HTMLSpanElement;
  return (
    <span
      ref={el}
      class="message-spoiler message-spoiler--hidden"
      onClick={() => el.classList.remove("message-spoiler--hidden")}
    >
      {props.text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Service lines (D71): the wording is the client's, in the reader's language.

function isKnownKind(kind: string): boolean {
  return (
    kind === "photo" ||
    kind === "video" ||
    kind === "voice" ||
    kind === "video_message" ||
    kind === "file"
  );
}

// How much of a name a service sentence prints before cutting it, and the cut
// itself. The line is a sentence, not a label, so the ellipsis has to belong to
// the name that is too long and never to the sentence around it: a capsule
// reading "MrZaZaMrZa…" says nothing about what happened, while
// "MrZaZaMrZa… removed Anya from the channel" still does. CSS cannot make that
// cut — the names are not elements of their own inside the line — so the
// capsule wraps and this keeps it to two or three lines. Mirrors `clip_name` in
// the desktop client's `state::feed`, 24 UTF-16 units and all.
const MAX_NAME_UNITS = 24;

function clipName(name: string): string {
  if (name.length <= MAX_NAME_UNITS) return name;
  let cut = MAX_NAME_UNITS - 1;
  // Never end on the high half of a surrogate pair: that is half an emoji.
  const code = name.charCodeAt(cut - 1);
  if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
  return `${name.slice(0, cut)}…`;
}

export function serviceSentence(
  service: Service,
  senderName: string,
  objectName: (userId: number) => string,
  t: Dict,
): string {
  const kind = (service as { kind: string }).kind;
  if (!SERVICE_KINDS.includes(kind as (typeof SERVICE_KINDS)[number])) {
    return t.svcUnknown();
  }
  const s = service as Record<string, unknown> & { kind: string };
  const who = clipName(senderName);
  const whom = () => clipName(objectName((s["user_id"] as number) ?? 0));
  switch (s.kind) {
    case "joined":
      return t.svcJoined(who);
    case "left":
      return t.svcLeft(who);
    case "removed":
      return t.svcRemoved(who, whom());
    case "admin_granted":
      return t.svcAdminGranted(who, whom());
    case "admin_revoked":
      return t.svcAdminRevoked(who, whom());
    case "renamed":
      return t.svcRenamed(who, clipName((s["name"] as string) ?? ""));
    case "banned":
      return t.svcBanned(who, whom(), untilText(s));
    case "unbanned":
      return t.svcUnbanned(who, whom());
    case "voice_muted":
      return t.svcVoiceMuted(who, whom(), untilText(s));
    case "voice_unmuted":
      return t.svcVoiceUnmuted(who, whom());
    case "owner_changed":
      return t.svcOwnerChanged(who, whom());
    default:
      return t.svcUnknown();
  }
}

function untilText(s: Record<string, unknown>): string | null {
  const until = s["until_ms"];
  if (typeof until !== "number") return null;
  return new Date(until).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ServiceLine(props: {
  bubble: Bubble;
  t: Dict;
  objectName: (userId: number) => string;
}): JSX.Element {
  const service = () => props.bubble.message.service as Service;
  const kind = () => (service() as { kind: string }).kind;

  return (
    <Show
      when={kind() === "call"}
      fallback={
        <div class="service-message">
          <span class="service-message__text">
            {serviceSentence(service(), props.bubble.senderName, props.objectName, props.t)}
          </span>
        </div>
      }
    >
      <CallCard bubble={props.bubble} t={props.t} />
    </Show>
  );
}

function CallCard(props: { bubble: Bubble; t: Dict }): JSX.Element {
  const log = () =>
    props.bubble.message.service as unknown as {
      participants: number[];
      live?: boolean;
      duration_s?: number | null;
    };
  const live = () => Boolean(log().live);
  return (
    <div class={`call-log-card${live() ? " call-log-card--live" : ""}`}>
      <span class="call-log-card__icon">
        <Icon name="call" size={17} filled />
      </span>
      <div class="call-log-card__column">
        <span class="call-log-card__title">
          {live()
            ? props.t.svcCallLive(log().participants.length)
            : props.t.svcCallEnded(
                log().duration_s != null ? props.t.durationS(log().duration_s!) : null,
              )}
        </span>
        <span class="call-log-card__subtitle">{props.t.svcCallStarted(props.bubble.senderName)}</span>
      </div>
    </div>
  );
}
